import {
  classifyToolError,
  reportLoopError,
} from './global-ai-loop-error-telemetry';
import { ArtifactCache, TurnState } from './global-ai-turn-messages';
import type {
  TurnMessage,
  TurnStepEvent,
  TurnStepRequestBody,
} from './global-ai-turn-step-stream';

const DEFAULT_MAX_STEPS = 6;

export type RunTurnLoopParams = {
  userPrompt: string;
  priorMessages: TurnMessage[];
  context?: TurnStepRequestBody['context'];
  postStep: (p: {
    body: TurnStepRequestBody;
    onEvent: (e: TurnStepEvent) => void;
    onInvalidEnvelope?: (raw: string, reason: 'json' | 'schema') => void;
    signal?: AbortSignal;
  }) => Promise<void>;
  executeTool: (
    name: string,
    input: unknown,
    artifacts: ArtifactCache
  ) => Promise<{ resultForModel: string; endsTurn?: boolean }>;
  onText: (segmentId: string, fullText: string) => void;
  onStatus: (content: string) => void;
  onError: (message: string) => void;
  onToolCall: (id: string, name: string, input: unknown) => void;
  onStepStart?: (roundId: string) => void;
  onStepDescribe?: (roundId: string, text: string) => void;
  onStepTool?: (
    roundId: string,
    name: string,
    input: unknown,
    artifacts?: ArtifactCache
  ) => void;
  onStepEnd?: (roundId: string, status: 'done' | 'error') => void;
  onAnswer?: (roundId: string, text: string) => void;
  signal?: AbortSignal;
  maxSteps?: number;
  newSegmentId?: () => string;
};

export async function runTurnLoop(params: RunTurnLoopParams): Promise<void> {
  const maxSteps = params.maxSteps ?? DEFAULT_MAX_STEPS;
  const newSegmentId = params.newSegmentId ?? (() => crypto.randomUUID());
  const state = new TurnState(params.priorMessages, params.userPrompt);
  const artifacts = new ArtifactCache();

  for (let stepIndex = 0; ; stepIndex += 1) {
    if (stepIndex >= maxSteps) {
      params.onError('This turn took too many steps; please try rephrasing.');
      return;
    }

    const segmentId = newSegmentId();
    params.onStepStart?.(segmentId);
    let streamedText = '';
    // Offset into `streamedText` where the CURRENT step's narration begins. A
    // round can run several tools (text → search_filters → more text →
    // render_chart); each tool's step description is only the narration emitted
    // since the previous tool, so we advance this past every tool boundary and
    // slice from it. `streamedText` itself stays cumulative (the model history
    // and the final answer need the whole round's text).
    let narrationStart = 0;
    let pendingToolCall:
      | { id: string; name: string; input: unknown }
      | undefined;
    let errored: string | undefined;

    await params.postStep({
      body: { messages: state.messages(), stepIndex, context: params.context },
      signal: params.signal,
      onEvent: (event) => {
        if (event.type === 'text') {
          streamedText += event.content;
          params.onText(segmentId, streamedText);
          params.onStepDescribe?.(
            segmentId,
            streamedText.slice(narrationStart)
          );
        } else if (event.type === 'status') {
          params.onStatus(event.content);
        } else if (event.type === 'artifact') {
          artifacts.put(event.id, event.payload);
        } else if (event.type === 'tool_call') {
          pendingToolCall = {
            id: event.id,
            name: event.name,
            input: event.input,
          };
        } else if (event.type === 'tool_step') {
          const stepId = `${segmentId}:${event.name}`;
          if (event.phase === 'start') {
            params.onStepStart?.(stepId);
            params.onStepTool?.(stepId, event.name, undefined, artifacts);
            // This server tool consumed the narration so far; the next step's
            // description starts from text emitted after it.
            narrationStart = streamedText.length;
          } else {
            params.onStepEnd?.(stepId, event.ok === false ? 'error' : 'done');
          }
        } else if (event.type === 'error') {
          errored = event.content;
        }
      },
      onInvalidEnvelope: (_raw, reason) => {
        // Additive telemetry + reuse of the EXISTING error path: set the same
        // `errored` flag a server `error` event would, so the loop's existing
        // post-stream handling surfaces it and ends the turn. No new control
        // flow is introduced.
        void reportLoopError({
          source: 'sse_envelope',
          errorClass: reason,
          stepIndex,
        });
        errored = 'The assistant sent a malformed response — please try again.';
      },
    });

    if (errored) {
      params.onError(errored);
      return;
    }
    if (!pendingToolCall) {
      params.onAnswer?.(segmentId, streamedText);
      return; // text-only step → turn complete
    }

    params.onToolCall(
      pendingToolCall.id,
      pendingToolCall.name,
      pendingToolCall.input
    );
    params.onStepTool?.(
      segmentId,
      pendingToolCall.name,
      pendingToolCall.input,
      artifacts
    );
    narrationStart = streamedText.length;
    state.recordAssistantStep(streamedText, {
      id: pendingToolCall.id,
      name: pendingToolCall.name,
      argumentsJson: JSON.stringify(pendingToolCall.input ?? {}),
    });

    // The offer tools (`navigate`, `create_custom_page`, `offer_page_or_custom`,
    // `offer_standard_page`) now carry their user-facing offer text in a
    // validated `narration` param, which the client-tool handlers append as the
    // assistant text (rendering the markers as inline triggers). On those turns
    // the model is told to emit NO free-text narration, so any streamed text is a
    // redundant preview — DROP it rather than flush it as an answer (belt-and-
    // suspenders against a stray preview duplicating the param narration).
    const isParamNarrationOffer =
      pendingToolCall.name === 'navigate' ||
      pendingToolCall.name === 'create_custom_page' ||
      pendingToolCall.name === 'offer_page_or_custom' ||
      pendingToolCall.name === 'offer_standard_page';

    // Other terminal tools that APPEND A CHIP (offer_report's report chips) must
    // render their streamed narration ABOVE the chip — the explanation comes
    // first, then the button. The chip is appended during executeTool, so without
    // this the chip lands first and the narration reads as a trailing afterthought
    // below it. Flush the narration BEFORE running the tool.
    const flushNarrationBeforeChip = pendingToolCall.name === 'offer_report';
    let offerNarrationFlushed = false;
    if (flushNarrationBeforeChip && streamedText.trim()) {
      params.onAnswer?.(segmentId, streamedText);
      offerNarrationFlushed = true;
    }

    let result: { resultForModel: string; endsTurn?: boolean };
    try {
      result = await params.executeTool(
        pendingToolCall.name,
        pendingToolCall.input,
        artifacts
      );
      // A client tool can also RETURN an `ERROR: …` string (artifact-not-found,
      // unknown-tool). Detect it here and emit telemetry without altering what
      // gets recorded/sent back to the model.
      if (
        typeof result.resultForModel === 'string' &&
        result.resultForModel.startsWith('ERROR:')
      ) {
        void reportLoopError({
          source: 'tool_execution',
          toolName: pendingToolCall.name,
          errorClass: classifyToolError(result.resultForModel),
          stepIndex,
        });
      }
    } catch (err) {
      // Additive telemetry — does not change the existing ERROR-result flow.
      void reportLoopError({
        source: 'tool_execution',
        toolName: pendingToolCall.name,
        errorClass: classifyToolError(err),
        stepIndex,
      });
      result = {
        resultForModel: `ERROR: ${
          err instanceof Error ? err.message : 'tool failed'
        }`,
      };
      params.onStepEnd?.(segmentId, 'error');
      state.recordToolResult(pendingToolCall.id, result.resultForModel);
      if (result.endsTurn) return;
      continue;
    }
    params.onStepEnd?.(segmentId, 'done');
    state.recordToolResult(pendingToolCall.id, result.resultForModel);
    if (result.endsTurn) {
      // A terminal tool (offer_report/ask_user) ends the turn while the round's
      // narration is the grounded answer. Surface it as the answer so it renders
      // once (not as a duplicate step description). Empty narration (e.g. a bare
      // ask_user with no preamble) stays answer-less.
      //
      // EXCEPTION: ask_user. Its user-facing output IS the clarifying question
      // (rendered by the askUser handler). Any streamed preamble is a stale
      // pre-action clause ("Generating a chart…") describing work the model is
      // NOT doing because it instead needs to ask — surfacing it as an answer
      // leaves a misleading "Generating…" message next to the question (FE-2171).
      //
      // Also skip the offer tools (`isParamNarrationOffer`): their user-facing
      // text is the param `narration` the handler already appended, so surfacing
      // the streamed preview here would duplicate it.
      if (
        streamedText.trim() &&
        pendingToolCall.name !== 'ask_user' &&
        !isParamNarrationOffer &&
        !offerNarrationFlushed
      ) {
        params.onAnswer?.(segmentId, streamedText);
      }
      return;
    }
  }
}
